import type { ChangeOrder } from "@/data/changeOrderTypes";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  FileText, CheckCircle, Clock, DollarSign, User, Calendar, Eye,
} from "lucide-react";
import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

interface Props {
  changeOrders: ChangeOrder[];
  currentContractValue: number;
}

const STATUS_STYLES: Record<string, string> = {
  Draft: "bg-muted text-muted-foreground border-border",
  "Pending Review": "bg-warning/10 text-warning border-warning/30",
  Signed: "bg-success/10 text-success border-success/30",
  Voided: "bg-destructive/10 text-destructive border-destructive/30",
};

export function ChangeOrderHistory({ changeOrders, currentContractValue }: Props) {
  const [viewOrder, setViewOrder] = useState<ChangeOrder | null>(null);

  if (changeOrders.length === 0) return null;

  const totalChangeAmount = changeOrders
    .filter(co => co.status === "Signed")
    .reduce((sum, co) => sum + co.changeAmount, 0);

  return (
    <>
      <div className="space-y-3">
        {/* Summary strip */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-4">
            <div className="text-xs">
              <span className="text-muted-foreground">Change Orders: </span>
              <span className="font-bold">{changeOrders.length}</span>
            </div>
            <div className="text-xs">
              <span className="text-muted-foreground">Total Adjustments: </span>
              <span className={`font-bold ${totalChangeAmount >= 0 ? "text-success" : "text-destructive"}`}>
                {totalChangeAmount >= 0 ? "+" : ""}${totalChangeAmount.toLocaleString()}
              </span>
            </div>
            <div className="text-xs">
              <span className="text-muted-foreground">Revised Contract: </span>
              <span className="font-bold text-primary">${currentContractValue.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Order list */}
        <div className="space-y-2">
          {changeOrders.map(co => (
            <div
              key={co.id}
              className="flex items-center justify-between rounded-lg border bg-card px-4 py-3 hover:bg-muted/20 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-1.5 rounded bg-primary/10 shrink-0">
                  <FileText className="h-3.5 w-3.5 text-primary" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold">CO-{co.orderNumber}</span>
                    <Badge variant="outline" className={`text-[9px] ${STATUS_STYLES[co.status] || ""}`}>
                      {co.status}
                    </Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate mt-0.5">{co.summary}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className={`text-xs font-bold ${co.changeAmount >= 0 ? "text-success" : "text-destructive"}`}>
                  {co.changeAmount >= 0 ? "+" : ""}${co.changeAmount.toLocaleString()}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {new Date(co.createdAt).toLocaleDateString()}
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setViewOrder(co)}>
                  <Eye className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!viewOrder} onOpenChange={() => setViewOrder(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Change Order CO-{viewOrder?.orderNumber}
            </DialogTitle>
          </DialogHeader>
          {viewOrder && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={STATUS_STYLES[viewOrder.status] || ""}>
                  {viewOrder.status}
                </Badge>
                <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(viewOrder.createdAt).toLocaleDateString()}
                </span>
                <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {viewOrder.createdBy}
                </span>
              </div>

              <div>
                <p className="text-xs font-semibold mb-1">Summary</p>
                <p className="text-xs text-muted-foreground">{viewOrder.summary}</p>
              </div>

              <div className="rounded-lg border bg-muted/20 p-3 space-y-1.5">
                {viewOrder.lineItems.map((li, idx) => (
                  <div key={idx} className="flex justify-between text-xs">
                    <span>{li.description}</span>
                    <span className="font-medium">
                      {li.amount >= 0 ? "+" : ""}${li.amount.toLocaleString()}
                    </span>
                  </div>
                ))}
                <div className="border-t pt-2 mt-2 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Original Contract</span>
                    <span>${viewOrder.originalContractValue.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Change Amount</span>
                    <span className={`font-bold ${viewOrder.changeAmount >= 0 ? "text-success" : "text-destructive"}`}>
                      {viewOrder.changeAmount >= 0 ? "+" : ""}${viewOrder.changeAmount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm font-bold border-t pt-1">
                    <span>Revised Total</span>
                    <span className="text-primary">${viewOrder.revisedContractValue.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Signatures */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Homeowner</p>
                  {viewOrder.homeownerSignature ? (
                    <>
                      <img src={viewOrder.homeownerSignature} alt="Homeowner signature" className="h-12 mx-auto" />
                      <p className="text-[10px] text-success mt-1 flex items-center justify-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Signed {viewOrder.homeownerSignedAt ? new Date(viewOrder.homeownerSignedAt).toLocaleDateString() : ""}
                      </p>
                    </>
                  ) : (
                    <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-1">
                      <Clock className="h-3 w-3" /> Pending
                    </p>
                  )}
                </div>
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Representative</p>
                  {viewOrder.repSignature ? (
                    <>
                      <img src={viewOrder.repSignature} alt="Rep signature" className="h-12 mx-auto" />
                      <p className="text-[10px] text-success mt-1 flex items-center justify-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Signed {viewOrder.repSignedAt ? new Date(viewOrder.repSignedAt).toLocaleDateString() : ""}
                      </p>
                    </>
                  ) : (
                    <p className="text-[10px] text-muted-foreground">—</p>
                  )}
                </div>
              </div>

              {viewOrder.notes && (
                <div>
                  <p className="text-xs font-semibold mb-1">Internal Notes</p>
                  <p className="text-xs text-muted-foreground">{viewOrder.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
